import {
  MutationFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { UUID } from 'crypto'

import * as API from '../../api/fundingNeedsAPI'

import {
  uploadFundingNeedFiles,
  fetchOrgFundingNeedDocs,
  deleteFundingNeedFile,
  uploadFundingNeedWebsites,
  refetchWebsite,
} from '@/api/docsAPI'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useAuth0 } from '@/utils/auth/auth0'

// Generic mutation hook for funding needs
function useFundingNeedMutation<TData = unknown, TVariables = unknown>(
  apiClientFn: MutationFunction<TData, TVariables>
) {
  const queryClient = useQueryClient()
  const { user } = getCurrentUser()

  return useMutation({
    mutationFn: apiClientFn,
    onSuccess: () => {
      // Invalidate all funding needs queries when any mutation succeeds
      queryClient.invalidateQueries({ queryKey: ['fundingNeeds'] })

      // Also invalidate onboarding data since funding needs affect onboarding status
      if (user?.uuid) {
        queryClient.invalidateQueries({
          queryKey: ['onboardingData'],
          exact: false, // This will match all variations including debug flag
        })
      }
    },
  })
}

// Individual mutation hooks
export function useCreateFundingNeedMutation() {
  return useFundingNeedMutation(API.createFundingNeed)
}

export function useDeleteFundingNeedMutation() {
  return useFundingNeedMutation(API.deleteFundingNeed)
}

export function useUpdateFundingNeedMutation() {
  return useFundingNeedMutation(API.updateFundingNeed)
}

export function useUpdateFundingNeedArchiveStatusMutation() {
  return useFundingNeedMutation(API.updateFundingNeedArchiveStatus)
}

// Main hook that combines query and mutations
export function useFundingNeeds(token: string | null, orgUuid: UUID | null) {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQuery({
    queryKey: ['fundingNeeds', orgUuid],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.getOrganisationFundingNeeds(freshToken, orgUuid!.toString())
    },
    enabled: !!token && !!orgUuid,
  })

  return {
    ...query,
    data: Array.isArray(query.data) ? query.data : undefined,
    createFundingNeed: useCreateFundingNeedMutation(),
    deleteFundingNeed: useDeleteFundingNeedMutation(),
    updateFundingNeed: useUpdateFundingNeedMutation(),
    updateFundingNeedArchiveStatus: useUpdateFundingNeedArchiveStatusMutation(),
  }
}

// Optional: Type for the return value of useFundingNeeds
export type UseFundingNeedsReturn = ReturnType<typeof useFundingNeeds>

function useFundingNeedDocsMutation<TData = unknown, TVariables = unknown>(
  apiClientFn: MutationFunction<TData, TVariables>,
  orgUuid: UUID,
  needId: number
) {
  const queryClient = useQueryClient()
  const { user } = getCurrentUser()

  return useMutation({
    mutationFn: apiClientFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['fundingNeedDocs', orgUuid, needId],
      })
      // Invalidate onboarding data to update wizard immediately
      if (user?.uuid) {
        queryClient.invalidateQueries({
          queryKey: ['onboardingData', user.uuid, false, orgUuid],
        })
      }
    },
  })
}

function useFundingNeedWebsiteMutation<TData = unknown, TVariables = unknown>(
  apiClientFn: MutationFunction<TData, TVariables>,
  orgUuid: UUID,
  needId: number
) {
  const queryClient = useQueryClient()
  const { user } = getCurrentUser()

  return useMutation({
    mutationFn: apiClientFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['fundingNeedDocs', orgUuid, needId],
      })
      // Invalidate onboarding data to update wizard immediately
      if (user?.uuid) {
        queryClient.invalidateQueries({
          queryKey: ['onboardingData', user.uuid, false, orgUuid],
        })
      }
    },
  })
}

export function useUploadFundingNeedDocMutation(orgUuid: UUID, needId: number) {
  return useFundingNeedDocsMutation(uploadFundingNeedFiles, orgUuid, needId)
}

export function useDeleteFundingNeedDocMutation(orgUuid: UUID, needId: number) {
  return useFundingNeedDocsMutation(deleteFundingNeedFile, orgUuid, needId)
}

export function useUploadOrgWebsiteMutation(orgUuid: UUID, needId: number) {
  return useFundingNeedWebsiteMutation(
    uploadFundingNeedWebsites,
    orgUuid,
    needId
  )
}

export function useRefetchFundingNeedWebsiteMutation(
  orgUuid: UUID,
  needId: number
) {
  return useFundingNeedWebsiteMutation(refetchWebsite, orgUuid, needId)
}

export function useFundingNeedDocs(
  orgUuid: UUID,
  token: string,
  needId: number
) {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQuery({
    queryKey: ['fundingNeedDocs', orgUuid, needId],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      const docs = await fetchOrgFundingNeedDocs(freshToken, orgUuid, needId)
      return docs
    },
    enabled: !!token && !!orgUuid && needId > 0,
    retry: 1,
  })

  return {
    ...query,
    data: Array.isArray(query.data) ? query.data : undefined,
    uploadFundingNeedDocs: useUploadFundingNeedDocMutation(orgUuid, needId),
    deleteFundingNeedDoc: useDeleteFundingNeedDocMutation(orgUuid, needId),
    uploadFundingNeedWebsites: useUploadOrgWebsiteMutation(orgUuid, needId),
    refetchFundingNeedWebsite: useRefetchFundingNeedWebsiteMutation(
      orgUuid,
      needId
    ),
  }
}
